import { useEffect, useRef, useState } from 'react'
import { db, supabase } from '../lib/supabase'

/**
 * Premium Context Menu Component
 * Glassmorphism right-click menu for users and messages
 * Provides quick actions based on permissions
 */
export default function ContextMenu({
  isOpen,
  position,
  onClose,
  type, // 'user' or 'message'
  target, // user object or message object
  currentUser,
  currentUserRole,
  spaceId,
}) {
  const menuRef = useRef(null)
  const [isProcessing, setIsProcessing] = useState(false)

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  // Close on ESC key
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  // Permission checks
  const isAdmin = ['owner', 'admin', 'moderator'].includes(currentUserRole)
  const isOwnContent = type === 'message' && target?.user_id === currentUser.id

  // Action handlers
  const handleAddFriend = async () => {
    setIsProcessing(true)
    try {
      const { error } = await db.friendships.sendRequest(target.friend_code, currentUser.id)
      if (error) throw error
      alert(`Friend request sent to ${target.username}!`)
      onClose()
    } catch (error) {
      console.error('Error sending friend request:', error)
      alert('Failed to send friend request')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleKickUser = async () => {
    if (!confirm(`Kick ${target.username} from this space?`)) return

    setIsProcessing(true)
    try {
      // Remove from members table
      const { error } = await supabase
        .from('members')
        .delete()
        .eq('space_id', spaceId)
        .eq('user_id', target.id)

      if (error) throw error

      // Log the action
      await supabase.from('context_action_logs').insert({
        actor_id: currentUser.id,
        target_id: target.id,
        action: 'kick',
        space_id: spaceId,
        reason: 'Kicked via context menu',
      })

      alert(`${target.username} has been kicked`)
      onClose()
    } catch (error) {
      console.error('Error kicking user:', error)
      alert('Failed to kick user')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleBanUser = async () => {
    const reason = prompt(`Ban ${target.username}? Enter reason:`)
    if (!reason) return

    setIsProcessing(true)
    try {
      // Remove from members table
      await supabase
        .from('members')
        .delete()
        .eq('space_id', spaceId)
        .eq('user_id', target.id)

      // Log the ban
      await supabase.from('context_action_logs').insert({
        actor_id: currentUser.id,
        target_id: target.id,
        action: 'ban',
        space_id: spaceId,
        reason,
      })

      alert(`${target.username} has been banned`)
      onClose()
    } catch (error) {
      console.error('Error banning user:', error)
      alert('Failed to ban user')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDeleteMessage = async () => {
    if (!confirm('Delete this message?')) return

    setIsProcessing(true)
    try {
      const { error } = await db.messages.delete(target.id)
      if (error) throw error

      if (!isOwnContent) {
        // Log admin deletion
        await supabase.from('context_action_logs').insert({
          actor_id: currentUser.id,
          target_id: target.user_id,
          action: 'delete_message',
          space_id: spaceId,
          reason: `Deleted message: "${target.content.substring(0, 50)}..."`,
        })
      }

      onClose()
    } catch (error) {
      console.error('Error deleting message:', error)
      alert('Failed to delete message')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleReplyToMessage = () => {
    // This will be handled by parent component
    onClose()
    if (window.replyToMessage) {
      window.replyToMessage(target)
    }
  }

  const handleStartThread = () => {
    // This will be handled by parent component
    onClose()
    if (window.startThread) {
      window.startThread(target)
    }
  }

  const handleCopyMessageLink = () => {
    const link = `${window.location.origin}/app?message=${target.id}`
    navigator.clipboard.writeText(link)
    alert('Message link copied!')
    onClose()
  }

  // Menu actions based on type
  const userActions = [
    {
      label: 'View Profile',
      icon: '👤',
      onClick: () => alert('Profile view coming soon!'),
      show: true,
    },
    {
      label: 'Add Friend',
      icon: '➕',
      onClick: handleAddFriend,
      show: target?.id !== currentUser.id,
    },
    {
      label: 'Send Message',
      icon: '💬',
      onClick: () => alert('DM feature coming soon!'),
      show: target?.id !== currentUser.id,
    },
    { divider: true, show: isAdmin },
    {
      label: 'Kick',
      icon: '🚪',
      onClick: handleKickUser,
      show: isAdmin && target?.id !== currentUser.id,
      danger: true,
    },
    {
      label: 'Ban',
      icon: '🔨',
      onClick: handleBanUser,
      show: isAdmin && target?.id !== currentUser.id && currentUserRole === 'owner',
      danger: true,
    },
  ]

  const messageActions = [
    {
      label: 'Reply',
      icon: '↩️',
      onClick: handleReplyToMessage,
      show: true,
    },
    {
      label: 'Start Thread',
      icon: '🧵',
      onClick: handleStartThread,
      show: !target?.parent_message_id,
    },
    {
      label: 'Copy Link',
      icon: '🔗',
      onClick: handleCopyMessageLink,
      show: true,
    },
    { divider: true, show: isOwnContent || isAdmin },
    {
      label: 'Delete Message',
      icon: '🗑️',
      onClick: handleDeleteMessage,
      show: isOwnContent || isAdmin,
      danger: true,
    },
  ]

  const actions = type === 'user' ? userActions : messageActions

  return (
    <div
      ref={menuRef}
      className="fixed z-50 glass-strong rounded-xl shadow-gjyl-lift p-2 min-w-[200px] animate-scale-in"
      style={{
        top: `${position.y}px`,
        left: `${position.x}px`,
      }}
    >
      {actions.map((action, index) => {
        if (!action.show) return null

        if (action.divider) {
          return (
            <div
              key={`divider-${index}`}
              className="h-px bg-gjyl-slate-700/50 my-1"
            />
          )
        }

        return (
          <button
            key={action.label}
            onClick={action.onClick}
            disabled={isProcessing}
            className={`
              w-full flex items-center space-x-3 px-3 py-2 rounded-lg
              text-sm font-medium transition-all duration-200
              ${action.danger
                ? 'text-red-400 hover:bg-red-500/10 hover:text-red-300'
                : 'text-slate-300 hover:bg-gjyl-slate-700/50 hover:text-white'
              }
              ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <span className="text-lg">{action.icon}</span>
            <span>{action.label}</span>
          </button>
        )
      })}

      {isProcessing && (
        <div className="absolute inset-0 bg-black/20 rounded-xl flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  )
}

/**
 * Hook to manage context menu state
 */
export function useContextMenu() {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [type, setType] = useState(null)
  const [target, setTarget] = useState(null)

  const open = (e, menuType, menuTarget) => {
    e.preventDefault()
    e.stopPropagation()

    // Calculate position (ensure menu stays on screen)
    const x = Math.min(e.clientX, window.innerWidth - 220)
    const y = Math.min(e.clientY, window.innerHeight - 300)

    setPosition({ x, y })
    setType(menuType)
    setTarget(menuTarget)
    setIsOpen(true)
  }

  const close = () => {
    setIsOpen(false)
    setTimeout(() => {
      setType(null)
      setTarget(null)
    }, 200)
  }

  return {
    isOpen,
    position,
    type,
    target,
    open,
    close,
  }
}
